import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import useApi from '../../hooks/useApi';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../../components/common';

const RoleForm = ({ open, role, onClose }) => {
  const api = useApi();
  const apiRef = useRef(api);

  const [formData, setFormData] = useState({
    roleName: '',
    roleCode: '',
  });
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);

  const [showNewPermission, setShowNewPermission] = useState(false);
  const [newPermission, setNewPermission] = useState({ permissionName: '', permissionCode: '' });

  useEffect(() => {
    apiRef.current = api;
  }, [api]);

  const loadData = useCallback(async (roleId = null) => {
    try {
      setLoading(true);
      
      const permResponse = await apiRef.current.get('/roles/permissions');
      const permApiResponse = permResponse.data;

      if (permApiResponse?.success && permApiResponse?.data) {
        setPermissions(permApiResponse.data.permissions || []);
      }

      if (roleId) {
        const roleResponse = await apiRef.current.get(`/roles/${roleId}`);
        const roleApiResponse = roleResponse.data;

        if (roleApiResponse?.success && roleApiResponse?.data) {
          const roleData = roleApiResponse.data;
          const permIds = roleData.permissions?.map(p => p.permissionId) || [];
          setSelectedPermissions(permIds);
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setError(null);
      setShowNewPermission(false);
      
      if (role) {
        setFormData({
          roleName: role.roleName || '',
          roleCode: role.roleCode || '',
        });
        loadData(role.roleId);
      } else {
        setFormData({ roleName: '', roleCode: '' });
        setSelectedPermissions([]);
        loadData();
      }
    }
  }, [open, role, loadData]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'roleName' && !role) {
      const code = value.toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
      setFormData(prev => ({ ...prev, roleCode: code }));
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const isManagementPermission = (permissionCode) => {
    const managementPermissions = ['manage_users', 'manage_tenants', 'manage_roles'];
    return managementPermissions.includes(permissionCode.toLowerCase());
  };

  const isJLLRole = (roleCode) => {
    return roleCode && roleCode.toUpperCase().startsWith('JLL_');
  };

  const isPermissionDisabled = (permission) => {
    // If this is a client role (not JLL role), disable management permissions
    if (formData.roleCode && !isJLLRole(formData.roleCode)) {
      return isManagementPermission(permission.permissionCode);
    }
    return false;
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // Filter out disabled permissions when selecting all
      const availablePermissions = permissions
        .filter(p => !isPermissionDisabled(p))
        .map(p => p.permissionId);
      setSelectedPermissions(availablePermissions);
    } else {
      setSelectedPermissions([]);
    }
  };

  const reloadPermissions = useCallback(async () => {
    try {
      const response = await apiRef.current.get('/roles/permissions');
      const apiResponse = response.data;
      if (apiResponse?.success && apiResponse?.data) {
        setPermissions(apiResponse.data.permissions || []);
      }
    } catch (err) {
      console.error('Error reloading permissions:', err);
    }
  }, []);

  const handleCreatePermission = async () => {
    if (!newPermission.permissionName || !newPermission.permissionCode) {
      setError('Permission name and code are required');
      return;
    }

    try {
      const response = await apiRef.current.post('/roles/permissions', newPermission);
      const apiResponse = response.data;

      if (apiResponse?.success) {
        setNewPermission({ permissionName: '', permissionCode: '' });
        setShowNewPermission(false);
        reloadPermissions();
      } else {
        setError(getApiErrorMessage(null, 'Failed to create permission'));
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to create permission'));
    }
  };

  const handleSubmit = async () => {
    if (!formData.roleName || !formData.roleCode) {
      setError('Role name and code are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        ...formData,
        permissionIds: selectedPermissions,
      };

      let response;
      if (role) {
        response = await apiRef.current.put(`/roles/${role.roleId}`, payload);
      } else {
        response = await apiRef.current.post('/roles', payload);
      }

      const apiResponse = response.data;

      if (apiResponse?.success) {
        onClose(true);
      } else {
        setError(getApiErrorMessage(null, 'Failed to save role'));
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save role'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6">{role ? 'Edit Role' : 'Create Role'}</Typography>
        <IconButton onClick={() => onClose(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>

        {formData.roleCode && !isJLLRole(formData.roleCode) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Client Role:</strong> Management permissions (Manage Users, Manage Tenants, Manage Roles) 
              are restricted to JLL roles only and cannot be assigned to client roles.
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Role Name"
            value={formData.roleName}
            onChange={handleChange('roleName')}
            required
            size="small"
            fullWidth
          />

          <TextField
            label="Role Code"
            value={formData.roleCode}
            onChange={handleChange('roleCode')}
            required
            size="small"
            fullWidth
            disabled={!!role}
            helperText={role ? 'Code cannot be changed' : 'Auto-generated from name'}
          />

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Permissions ({selectedPermissions.length}/{permissions.length})
            </Typography>
            <Box display="flex" gap={1}>
              <Button size="small" onClick={() => handleSelectAll(selectedPermissions.length !== permissions.length)}>
                {selectedPermissions.length === permissions.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setShowNewPermission(!showNewPermission)}>
                New
              </Button>
            </Box>
          </Box>

          {showNewPermission && (
            <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Create New Permission</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  label="Name"
                  value={newPermission.permissionName}
                  onChange={(e) => setNewPermission(prev => ({
                    ...prev,
                    permissionName: e.target.value,
                    permissionCode: e.target.value.toLowerCase().replace(/\s+/g, '_')
                  }))}
                  sx={{ flex: 1, minWidth: 150 }}
                />
                <TextField
                  size="small"
                  label="Code"
                  value={newPermission.permissionCode}
                  onChange={(e) => setNewPermission(prev => ({ ...prev, permissionCode: e.target.value }))}
                  sx={{ flex: 1, minWidth: 120 }}
                />
                <Button variant="contained" size="small" onClick={handleCreatePermission}>Add</Button>
                <Button size="small" onClick={() => setShowNewPermission(false)}>Cancel</Button>
              </Box>
            </Box>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ maxHeight: '40vh', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
              <FormGroup>
                {permissions.map(perm => {
                  const isDisabled = isPermissionDisabled(perm);
                  const isChecked = selectedPermissions.includes(perm.permissionId);
                  
                  return (
                    <FormControlLabel
                      key={perm.permissionId}
                      disabled={isDisabled}
                      control={
                        <Checkbox
                          size="small"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={() => handlePermissionToggle(perm.permissionId)}
                        />
                      }
                      label={
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: isDisabled ? 'text.disabled' : 'text.primary',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            {perm.permissionName}
                            {isDisabled && (
                              <Chip 
                                label="JLL Only" 
                                size="small" 
                                sx={{ 
                                  height: 18, 
                                  fontSize: '0.65rem',
                                  bgcolor: '#ffeaa7',
                                  color: '#2d3436'
                                }} 
                              />
                            )}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color={isDisabled ? 'text.disabled' : 'text.secondary'}
                          >
                            {perm.permissionCode}
                          </Typography>
                        </Box>
                      }
                    />
                  );
                })}
              </FormGroup>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => onClose(false)} variant="outlined">Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !formData.roleName || !formData.roleCode}
          startIcon={saving && <CircularProgress size={16} color="inherit" />}
          sx={{ bgcolor: '#000', '&:hover': { bgcolor: '#333' } }}
        >
          {role ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleForm;
